'use client'

import { type DragEvent } from 'react'
import {
  Phone,
  Bot,
  Users,
  Voicemail,
  Volume2,
  Clock,
  PhoneOff,
  Plus,
  ChevronDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import type { NodeType } from '@/lib/pbx/flow/types'
import { useTranslations } from "next-intl"



interface NodeTypeConfig {
  type: NodeType
  label: string
  icon: typeof Phone
  description: string
  color: string
}

function getnodeTypes(tUi: ReturnType<typeof useTranslations<"Pbx.PbxFlowEditorFlowSidebar">>): NodeTypeConfig[] {
  return [
  {
    type: 'incoming_call',
    label: tUi("incomingCall"),
    icon: Phone,
    description: tUi("entryPoint"),
    color: 'border-green-500 bg-green-50 dark:bg-green-950/30',
  },
  {
    type: 'ai_agent',
    label: tUi("voiceAgent"),
    icon: Bot,
    description: tUi("aIVoiceAssistant"),
    color: 'border-purple-500 bg-purple-50 dark:bg-purple-950/30',
  },
  {
    type: 'operator',
    label: tUi("operator"),
    icon: Users,
    description: tUi("forwardToOperator"),
    color: 'border-blue-500 bg-blue-50 dark:bg-blue-950/30',
  },
  {
    type: 'voicemail',
    label: tUi("voicemail"),
    icon: Voicemail,
    description: tUi("recordMessage"),
    color: 'border-orange-500 bg-orange-50 dark:bg-orange-950/30',
  },
  {
    type: 'play_message',
    label: tUi("playMessage"),
    icon: Volume2,
    description: tUi("tTSOrAudio"),
    color: 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30',
  },
  {
    type: 'time_check',
    label: tUi("timeCheck"),
    icon: Clock,
    description: tUi("scheduleRouting"),
    color: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30',
  },
  {
    type: 'hangup',
    label: tUi("hangup"),
    icon: PhoneOff,
    description: tUi("endCall"),
    color: 'border-red-500 bg-red-50 dark:bg-red-950/30',
  },
]
}

interface FlowSidebarProps {
  className?: string
  onAddNode: (type: NodeType) => void
}

export function FlowSidebar({ className, onAddNode }: FlowSidebarProps) {

  const tUi = useTranslations("Pbx.PbxFlowEditorFlowSidebar")
  const nodeTypes = getnodeTypes(tUi)


  const onDragStart = (event: DragEvent<HTMLDivElement>, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType)
    event.dataTransfer.effectAllowed = 'move'
  }

  return (
    <>
      <div className="shrink-0 border-b bg-background p-3 md:hidden">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span className="flex items-center gap-2"><Plus className="h-4 w-4" /><span key={tUi("addBlock")}>{tUi("addBlock")}</span></span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] max-w-[calc(100vw-1.5rem)]">
            {nodeTypes.map((node) => {
              const Icon = node.icon
              return (
                <DropdownMenuItem key={node.type} onSelect={() => onAddNode(node.type)} className="gap-3 py-3">
                  <Icon className="h-5 w-5 shrink-0" />
                  <span>{node.label}</span>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    <div className={cn('hidden w-64 shrink-0 overflow-y-auto border-r bg-background p-4 md:block', className)}>
      <h3 className="font-semibold text-sm mb-4"><span key={tUi("blocks")}>{tUi("blocks")}</span></h3>
      <div className="space-y-2">
        {nodeTypes.map((node) => {
          const Icon = node.icon
          return (
            <div
              key={node.type}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border cursor-grab transition-colors',
                'hover:shadow-sm active:cursor-grabbing',
                node.color
              )}
              draggable
              onDragStart={(e) => onDragStart(e, node.type)}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-sm">{node.label}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {node.description}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
    </>
  )
}
